/* -----------------UI--------------- */
import { Stack, useFocusEffect, useLocalSearchParams } from "expo-router";

/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";
import { useConsent } from "@hooks/useConsent";
import { Redirect } from "expo-router";

/* -----------------Child components--------------- */

import OnboardingAppBar from "@features/auth/components/OnboardingAppBar";
import EmailContainer from "@features/auth/containers/EmailContainer";
import { handleNavigateBack } from "@helpers/common";
import { buildType } from "@helpers/expo-extras";
import { replayManager } from "@helpers/lib/sentry";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { useDevice } from "@hooks/useDevice";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { baseDevUrl, isDev } from "@memorang/constants/src/urls";
import { useToast } from "@memorang/ui/src/components/Toast";
/* -----------------Analytics--------------- */
import { trackCustomEvent } from "analytics";
import DocumentTitle from "components/DocumentTitle";
import { useCallback } from "react";

const LoginPage = () => {
	useFocusEffect(
		useCallback(() => {
			replayManager().startReplay();
		}, []),
	);

	const companyName = useAppStore((store) => store.companyName);
	const supportEmail = useAppStore((store) => store.supportEmail);
	const baseUrl = useAppStore((store) => store.app.links.baseUrl);
	const app = useAppStore((store) => store.app);
	const tenant = useAppStore((store) => store.tenant);
	const tos = useAppStore((store) => store.app.links.tos);
	const privacy = useAppStore((store) => store.app.links.privacy);
	const currentAppVersion = useAppStore((state) => state.app.version);
	const { isMobile } = useDevice();
	const { showToast } = useToast();
	const { requiresExplicitConsent } = useConsent();
	const pendingConsent = useGlobalStore((state) => state.pendingConsent);

	const appId = app.id;
	const tenantId = tenant.id;

	const baseRedirectUrl = isDev ? baseDevUrl : baseUrl;

	const lastLoggedInEmail = useGlobalStore((state) => state.lastLoggedInEmail);
	const params = useLocalSearchParams<{
		redeemCode: string;
		email: string;
	}>();
	// useEffect(() => {
	// 	trackPageView({ type: pageViewTypes.login });
	// }, []);

	const title = "Continue with email";

	const emailFromUrl = params?.email
		? decodeURIComponent(params?.email).replace(/ /g, "+")
		: "";

	const onError = (message: string) => {
		showToast({ message });
	};

	const logError = (error: unknown, title: string) => {
		const errorObj = new Error(title);
		captureErrorToSentry(errorObj, {
			error: JSON.stringify(error),
		});
	};

	if (requiresExplicitConsent && !pendingConsent) {
		return <Redirect href="/" />;
	}

	return (
		<>
			<Stack.Screen
				options={{
					headerShown: true,
					header: () => {
						return (
							<OnboardingAppBar
								showLogo={!isMobile}
								title={title}
								onBackAction={() => handleNavigateBack("/")}
							/>
						);
					},
				}}
			/>
			<DocumentTitle title="Login" />
			<EmailContainer
				handleTracking={trackCustomEvent}
				companyName={companyName}
				lastLoggedInEmail={lastLoggedInEmail}
				supportEmail={supportEmail}
				buildType={buildType}
				appId={appId}
				tenantId={tenantId}
				redirectUrl={`${baseRedirectUrl}/verify`}
				currentAppVersion={currentAppVersion!}
				emailFromUrl={emailFromUrl}
				tos={tos}
				privacy={privacy}
				logError={logError}
				onError={onError}
			/>
		</>
	);
};

export default LoginPage;
